import { OPERATORS } from '../../constants';
import { BaseOperatorStrategy } from './base.operator-strategy';
import type {
  SelectionBlockerArgs,
  SelectionBlockerResult,
  UnitCostCalculationArgs,
} from '../operator-strategy';

/**
 * ICEYE strategy — handles POI/AOI mode disabling, default mode selection, and pricing.
 *
 * Mode classification:
 *   Spotlight modes: captureMode starts with "Spotlight" (SPOT, SPOT_FINE,
 *     SPOT_EXTENDED_AREA, DWELL, DWELL_FINE, DWELL_PRECISE) → POI only
 *   Non-spotlight modes: STRIPMAP ("Stripmap Strip") and ScanSAR variants → AOI only
 *
 * Pricing: reads selectedModes[0].pricing[latencyKey] — flat per-latency lookup.
 */
export class IceyeOperatorStrategy extends BaseOperatorStrategy {
  readonly operatorKey = OPERATORS.ICEYE;

  override applyResolutionConstraints(
    sensorRow: any,
    productMode: any,
  ): boolean {
    if (!Array.isArray(sensorRow?.resolution)) return false;
    const targetDistance = productMode?.distance;
    sensorRow.selectedResolutionData = null;
    for (const item of sensorRow.resolution) {
      if (item.value === targetDistance) {
        item.disable = false;
        sensorRow.selectedResolution = item;
        sensorRow.selectedResolutionValue = item.value;
      } else {
        item.disable = true;
      }
    }

    // Sync ONA selection from the mode's minONA/maxONA fields.
    if (
      Array.isArray(sensorRow.ONA) &&
      productMode?.minONA != null &&
      productMode?.maxONA != null
    ) {
      const match = sensorRow.ONA.find(
        (o: any) =>
          o.minONA === productMode.minONA && o.maxONA === productMode.maxONA,
      );
      if (match) {
        sensorRow.selectedONA = match.key;
        sensorRow.selectedItem = match;
        sensorRow.selectedONAOption = match;
      }
    }

    return true;
  }

  override syncLatencyCosts(sensorRow: any): void {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return;
    const pricingArray = Array.isArray(sensorRow.selectedModes)
      ? sensorRow.selectedModes[0]?.pricing
      : sensorRow.selectedModes?.pricing;
    if (!pricingArray) return;

    // Preserve the full latency list on first call so subsequent mode changes can restore hidden tiers.
    if (!sensorRow._allLatency) {
      sensorRow._allLatency = [...sensorRow.latency];
    }

    // Rebuild from the original list each time, keeping only tiers with a non-null price in this mode.
    sensorRow.latency = sensorRow._allLatency.filter(
      (latencyItem: any) =>
        Object.prototype.hasOwnProperty.call(pricingArray, latencyItem.key) &&
        pricingArray[latencyItem.key] != null,
    );

    for (const latencyItem of sensorRow.latency) {
      const newCost = Number(pricingArray[latencyItem.key]);
      latencyItem.cost = newCost;
      latencyItem.unitcost = newCost;
    }
  }

  isOnaDisabled(onaOption: any, sensorRow: any): boolean {
    const mode = Array.isArray(sensorRow?.selectedModes)
      ? sensorRow.selectedModes[0]
      : sensorRow?.selectedModes;
    if (!mode || onaOption?.minONA == null || onaOption?.maxONA == null)
      return false;
    return onaOption.minONA !== mode.minONA || onaOption.maxONA !== mode.maxONA;
  }

  resolveActiveLatency(sensorRow: any): any | null {
    if (!Array.isArray(sensorRow?.latency) || sensorRow.latency.length === 0)
      return null;
    const current = sensorRow.latency.find(
      (item: any) => item.selected === true,
    );
    if (current && (current.cost || 0) > 0) return current;
    return sensorRow.latency.find((item: any) => (item.cost || 0) > 0) ?? null;
  }

  getSelectionBlocker(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    if (args?.taskingContext?.isRecurrent) {
      return this.createUnsupportedRecurrencyBlocker(
        args?.sensorRow,
        'ICEYE_RECURRENCY_UNSUPPORTED',
      );
    }
    return (
      this.getAoiPoiFlagBlocker(args) ??
      this.getMinStartHoursBlocker(args) ??
      this.getMaxEndDaysBlocker(args) ??
      this.getMinDurationHoursBlocker(args)
    );
  }

  getSelectionWarning(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    return this.getCancellationPeriodWarning(args);
  }

  private getCancellationPeriodWarning(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    const timeDiffHours =
      (args.toiStartUtc.getTime() - this.resolveSubmissionDate().getTime()) /
      3_600_000;

    if (timeDiffHours >= 72) return null;

    return {
      code: 'ICEYE_CANCELLATION_WARNING',
      message:
        "You will be submitting this task within the operator's cancelation penalty period. Please see the cancelation policy next to the sensor name.",
    };
  }

  private getMinStartHoursBlocker(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    const sensorRow = args?.sensorRow;
    const minStartHours: number = sensorRow?.minStartHours ?? 12;
    const timeDiffHours =
      (args.toiStartUtc.getTime() - this.resolveSubmissionDate().getTime()) /
      3_600_000;
    if (timeDiffHours >= minStartHours) return null;
    return {
      code: 'ICEYE_TOI_MIN_START_HOURS',
      message: `We are unable to accept this task as ${sensorRow?.displayName} ${sensorRow?.sensor} requires start time to be current date + ${minStartHours}hrs. Please change your time of interest (TOI) or choose an alternative provider.`,
    };
  }

  private getMaxEndDaysBlocker(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    if (!args.toiEndUtc) return null;
    const sensorRow = args?.sensorRow;
    const maxEndDays: number = sensorRow?.maxEndDays ?? 14;
    const daysFromNowToEnd = Math.ceil(
      (args.toiEndUtc.getTime() - this.resolveSubmissionDate().getTime()) /
        86_400_000,
    );
    if (daysFromNowToEnd <= maxEndDays) return null;
    return {
      code: 'ICEYE_TOI_MAX_END_DAYS',
      message: `${sensorRow?.displayName} ${sensorRow?.sensor} requires the time of interest (TOI) to be ${maxEndDays} days or less from the current date. Please change your TOI or choose an alternative provider.`,
    };
  }

  private getMinDurationHoursBlocker(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    if (!args.toiEndUtc) return null;
    const sensorRow = args?.sensorRow;
    const minDurationHours: number = sensorRow?.minDurationHours ?? 6;
    const durationHours =
      (args.toiEndUtc.getTime() - args.toiStartUtc.getTime()) / 3_600_000;
    if (durationHours >= minDurationHours) return null;
    return {
      code: 'ICEYE_TOI_MIN_DURATION_HOURS',
      message: `${sensorRow?.displayName} ${sensorRow?.sensor} requires a minimum of a ${minDurationHours}-hour window for collection. If you are tasking with a period-frequency across two dates, please ensure there are ${minDurationHours} hrs between the start and the end time. If you have chosen to do the task on a specific date or day with less than a ${minDurationHours}-hour window and wish to extend the time window to 6 hours, please click Agree.`,
      confirm: true,
    };
  }

  protected computeBaseCost(
    sensorRow: any,
    latencyIndx: number,
  ): number | null {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return null;
    this.syncLatencyCosts(sensorRow);
    return Number(sensorRow.latency[latencyIndx]?.cost ?? 0);
  }
}
